/* eslint-disable no-use-before-define */
/* eslint-disable no-param-reassign */
import { batch } from 'react-redux';
import * as ReactRouter from 'connected-react-router';
import * as queryString from 'query-string';
import omit from 'lodash/omit';
import MATCHES_CONSTANTS, { MATCH_FLOWS } from 'modules/matches/constants';
import { providerActions } from 'modules/provider/actions';
import MAIN_CONSTANTS from 'utils/constants/main';
import PARTNER_CONSTANTS from 'utils/constants/partner';
import INTERNAL_CONSTANTS from 'utils/constants/internal';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { RESPONSE_ERROR } from 'utils/constants/responseError';
import { LOCAL_STORAGE_KEYS } from 'utils/constants/localStorageKeys';
import { AUTH_TYPE } from 'utils/constants/AuthenticationType';
import {
  PROVIDER_URL_SEARCH_PARAM,
  QUESTIONNAIRE_IN_PROGRESS_URL_SEARCH_PARAM,
  SIGNUP_TYPE_URL_SEARCH_PARAM,
  AUTH_TYPE_URL_SEARCH_PARAM,
  REMATCH_URL_SEARCH_PARAM,
  AUTH_EMAIL_URL_SEARCH_PARAM,
  USE_QUESTIONNAIRE_SELECTOR_URL_SEARCH_PARAM,
} from 'utils/constants/urlSearchParameters';
import { getQuestionnaireQuestions } from 'components/Questionnaire/functions';
import { axiosExceptionToFailure } from 'functions/axiosExceptionToFailure';
import { getLocalStorageUser } from 'functions/getLocalStorageUser';
import setJoinedAt from 'functions/setJoinedAt';
import { preserveQueryParam } from 'functions/preserveQueryParam';
import { clearAuthDataInLocalStorage } from 'functions/clearAuthDataInLocalStorage';
import { uploadQuestionnaireFiles, resetUploadedQuestionnaireFiles, setIsFetching } from 'components/Questionnaire/actions';
import { setKeyValue } from 'modules/keyValueStore/actions';
import { appConfigurationActions } from 'modules/appConfiguration/actions';
import { showBackdrop, hideBackdrop } from 'modules/backdrop/actions';
import { BACKDROP_TYPES } from 'modules/backdrop/constants';
import { getUserWixId } from 'modules/user/utils';
import { selectQuestionnaireFilesToUpload } from 'components/Questionnaire/selectors';
import { MatchFlow } from 'modules/matches/matchFlow';
import analytics from 'functions/analytics';
import { POPUP } from 'utils/constants/popup';
import { FINISHED_SIGNUP_AT_KEY } from 'utils/constants/storeGlobalKey';
import { intercom, INTERCOM_EVENTS } from 'modules/intercom/intercom';
import { matchesActions } from 'modules/matches/actions';
import { createLoginToken } from 'modules/user/api';
import phone from 'phone';
import app from 'new-ui/app';
import {
  GA_LOGIN_CATEGORY,
  GA_LOGIN_EVENTS,
  GA_QUESTIONNAIRE_CATEGORY,
  GA_QUESTIONNAIRE_EVENTS,
  GA_GENERAL_POPUPS_EVENTS,
  GA_GENERAL_CATEGORY,
} from 'modules/ga/generalEvents';
import { isArray, isEqual } from 'lodash';
import { updateUserChanges } from 'functions/updateUserChanges';
import { displayDashboardSkeleton, removeDashboardSkeleton } from 'new-ui/functions';
import sendAuthEvent from 'functions/analytics/functions/sendAuthEvent';
import { assembleRoutePath } from 'functions/assembleRoutePath';
import { geoActions } from 'utils/actions/geo';
import { authenticationApi } from './api';
import CONSTANTS from './constants';
import {
  createQuestionnaireRedirectURL, setTokens, setSession, formLocalStorageUserFromServerUser, formLoginResponseProfile,
} from './functions';

const { clearGeoLocation, setGeoLocation } = geoActions;

const sendFormRequest = () => ({ type: CONSTANTS.SEND_FORM_REQUEST });
const sendFormSuccess = () => ({ type: CONSTANTS.SEND_FORM_SUCCESS });

const sendVerificationCodeRequest = () => ({ type: CONSTANTS.SEND_VERIFICATION_CODE_REQUEST });
const sendVerificationCodeSuccess = () => ({ type: CONSTANTS.SEND_VERIFICATION_CODE_SUCCESS });

function setProfileId(value) {
  return { type: CONSTANTS.SET_PROFILE_ID, payload: value };
}

const userAuthenticationFailure = (errorMessage) => ({ type: CONSTANTS.USER_AUTHENTICATION_FAILURE, payload: errorMessage || null });

export const userAuthenticationSuccess = (user, justSignedUp) => ({
  type: CONSTANTS.SET_AUTHENTICATION_DATA,
  payload: {
    user,
    justSignedUp: justSignedUp || false,
  },
});

const resetFormValidity = () => ({ type: CONSTANTS.RESET_FORM_VALIDITY });

const setFormValidity = (data) => ({ type: CONSTANTS.SET_FORM_VALIDITY, payload: data });

const setPhoneConfirmationResponse = (data) => ({ type: CONSTANTS.SET_PHONE_CONFIRMATION_RESPONSE, payload: data });

const resetPhoneConfirmationResponse = () => ({ type: CONSTANTS.RESET_PHONE_CONFIRMATION_RESPONSE });

const resetLoggedIn = () => ({ type: CONSTANTS.RESET_LOGGED_IN });

export const upsertUserPatient = (data) => ({ type: CONSTANTS.UPSERT_USER_PATIENT, payload: data });
export const setUserData = (data, meta) => ({ type: CONSTANTS.SET_USER_DATA, payload: data, meta });

const requestUserUpdate = (meta) => ({ type: CONSTANTS.UPDATE_USER_REQUEST, meta });
const successUserUpdate = (meta) => ({ type: CONSTANTS.UPDATE_USER_SUCCESS, meta });
const failureUserUpdate = (payload, meta) => ({ type: CONSTANTS.UPDATE_USER_FAILURE, payload, meta });

const clearMatches = () => ({ type: MATCHES_CONSTANTS.CLEAR_MATCHES });

export const setIsNewProfile = (payload) => ({
  type: MAIN_CONSTANTS.SET_IS_NEW_PROFILE,
  payload,
});

const setNewProfile = (user) => ({ type: CONSTANTS.SET_NEW_PROFILE, payload: user });

const requestSignup = (data, type) => async (dispatch, getState) => {
  const { customerRef, condition } = data?.personal || {};

  dispatch(sendFormRequest());

  let result = null;
  try {
    result = await authenticationApi.signup(data, type);
  } catch (ex) {
    result = axiosExceptionToFailure(ex);
    console.error('requestSignup error:', ex);
  }

  if (!result?.success && result?.code === RESPONSE_ERROR.USER_EXISTS) {
    // User already exists. Redirect to login page

    dispatch(userAuthenticationFailure(null));
    dispatch(sendFormSuccess());
    dispatch(setProfileId(null));

    intercom.sendEvent(INTERCOM_EVENTS.QUICK_SIGNUP.QUICK_SIGNUP, { quickSignUp: 'returned user' });

    const logoutUrl = type
      ? `${ROUTE_PATH[ROUTE_NAME.LOGIN]}`
      : `${ROUTE_PATH[ROUTE_NAME.LOGIN]}?${AUTH_TYPE_URL_SEARCH_PARAM}=${data?.info?.email ? AUTH_TYPE.EMAIL : AUTH_TYPE.PHONE}`;

    dispatch(logout(logoutUrl));
    return;
  }
  // if response has an error
  if (!result?.success) {
    console.error('Error occurred, while sending signing up request');
    const errDisplayMsg = result
      && typeof result.message === 'string'
      && result.message.startsWith('authentication.error')
      ? result.message
      : 'authentication.error.wrong_auth_data';
    dispatch(userAuthenticationFailure(errDisplayMsg));
    return;
  }

  if (!result.data.access_token || !result.data.refresh_token || !result.data.user) {
    // should be invariant
    console.error('Wrong response from the profile server');
    dispatch(userAuthenticationFailure());
    return;
  }

  // signup success

  if (window.google?.accounts?.id) {
    window.google.accounts.id.cancel();
  }

  setTokens(result.data.access_token, result.data.refresh_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER, result.data.user);
  const user = JSON.parse(result?.data?.user);
  setSession(user);

  if (data?.personal) {
    user.personal = {
      ...data.personal,
      user_wix_id: user.user_wix_id,
    };
  }

  // This sets LOCAL_STORAGE_KEYS.JOINED_AT if doesn't exist
  setJoinedAt();

  dispatch(userAuthenticationSuccess(user, true));
  dispatch(setNewProfile(user));

  let userData;
  try {
    // WEBAPP-3801 will fill "profile.condition_profile" as result ->  will allow to edit existing profile
    userData = await dispatch(getUser());
    if (!userData) {
      throw new Error('empty user response');
    }
  } catch (ex) {
    console.error('after signup operation error.', ex);
  }

  // looks like it is not the case any longer
  // files uploading is not available before authentication
  // depends on profile
  dispatch(uploadQuestionnaireFiles()).catch((ex) => {
    console.error('requestSignup uploadQuestionnaireFiles error', ex);
  });

  sendAuthEvent(type);

  const {
    user_wix_id: userWixId,
    email,
  } = user;

  let loginToken;
  try {
    loginToken = await createLoginToken();
    // eslint-disable-next-line no-empty
  } catch (e) {
  }
  if (!loginToken) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.SHOULD_CREATE_LOGIN_TOKEN, '1');
  }

  intercom.update({
    user: data,
    data: {
      email,
      user_id: userWixId,
      quick_signup: true,
      token: loginToken,
      intake_engagement: false,
    },
  });
  intercom.sendEvent(INTERCOM_EVENTS.SIGNUP.SUCCEED);

  if (customerRef) {
    batch(() => {
      dispatch(hideBackdrop({
        minDisplayTime: 5000,
        callback: () => {
          dispatch(ReactRouter.replace(ROUTE_PATH[ROUTE_NAME.FEED]));
        },
      }));
      dispatch(sendFormSuccess());
    });

    return;
  }

  analytics.quickSignUpSucceed();

  await dispatch(matchesActions.getMatches({ matchFlow: MATCH_FLOWS.BASIC }));

  const {
    matches: {
      error: gettingTrialsMatchesError, result: userTrials,
    },
  } = getState();
  if (gettingTrialsMatchesError) {
    dispatch(logout());
    return;
  }

  intercom.update({ user: data, userTrials });

  if (AUTH_TYPE.GOOGLE === type?.toUpperCase()) {
    app.sendGoogleAnalyticsEvent(
      GA_QUESTIONNAIRE_CATEGORY,
      `${GA_QUESTIONNAIRE_EVENTS.QUICK_SIGNUP_SUCCESS_INTAKE}_${condition}`,
    );
  }

  batch(() => {
    dispatch(ReactRouter.push(ROUTE_PATH[ROUTE_NAME.AFTER_SIGNUP_USER_DETAILS]));
    dispatch(sendFormSuccess());
  });
};

const finishAfterSignupUserUpdate = ({ shouldRefreshMatches }) => async (dispatch, getState) => {
  const { profile: { data: profile } } = getState();

  const {
    personal: {
      condition,
    },
  } = profile;

  if (shouldRefreshMatches) {
    await dispatch(matchesActions.getMatches({ matchFlow: MATCH_FLOWS.BASIC }));

    const {
      matches: {
        error: gettingTrialsMatchesError, result: userTrials,
      },
    } = getState();
    if (gettingTrialsMatchesError) {
      dispatch(logout());
      return;
    }

    intercom.update({ user: profile, userTrials });
  }

  app.sendGoogleAnalyticsEvent(
    GA_QUESTIONNAIRE_CATEGORY,
    `${GA_QUESTIONNAIRE_EVENTS.QUICK_SIGNUP_SUCCESS_INTAKE}_${condition}`,
  );

  batch(() => {
    dispatch(ReactRouter.replace(`${ROUTE_PATH[ROUTE_NAME.RESULTS]}`));
    dispatch(setKeyValue(FINISHED_SIGNUP_AT_KEY, new Date().toJSON()));
  });
};

const updateUserAfterSignup = ({
  userToUpdate, onUserUpdateFulfilled, shouldFinish, mayMatchesBeAffected,
}) => async (dispatch, getState) => {
  let isUserUpdateFailure = false;
  if (userToUpdate) {
    dispatch(showBackdrop());

    const { info: { country } = {} } = userToUpdate;
    if (country) {
      dispatch(geoActions.setGeoLocation(country));
    }

    await dispatch(authenticationActions.updateUser({ user: userToUpdate }));

    dispatch(hideBackdrop());

    const { userUpdate: { error: userUpdateError } } = getState();

    if (userUpdateError) {
      isUserUpdateFailure = true;
    }
  }

  onUserUpdateFulfilled({ isUserUpdateFailure });

  if (!shouldFinish || isUserUpdateFailure) {
    return;
  }

  // finish after signup user update flow

  const isDashboardSkeletonShown = !!mayMatchesBeAffected;

  if (isDashboardSkeletonShown) {
    displayDashboardSkeleton();
  }

  await dispatch(finishAfterSignupUserUpdate({ shouldRefreshMatches: mayMatchesBeAffected }));
  if (isDashboardSkeletonShown) {
    removeDashboardSkeleton();
  }
};

const finishLogin = ({
  navigationPath,
  matchFlow = MATCH_FLOWS.LATEST,
} = {}) => async (dispatch, getState) => {
  const questionnaireFilesToUpload = selectQuestionnaireFilesToUpload(getState());
  const isSplashNeeded = !!questionnaireFilesToUpload.length;
  if (isSplashNeeded) {
    dispatch(showBackdrop({ type: BACKDROP_TYPES.SPLASH }));
  }

  let userData;
  try {
    userData = await dispatch(getUser());
    if (!userData) {
      throw new Error('empty user response');
    }
  } catch (ex) {
    console.error('after login operation error.', ex);
  }

  // looks like it is not the case any longer
  // files uploading is not available before authentication
  // depends on profile
  dispatch(uploadQuestionnaireFiles()).catch((ex) => {
    console.error('requestLogin uploadQuestionnaireFiles error', ex);
  });

  let finalNavigationPath = navigationPath;
  if (!finalNavigationPath) {
    finalNavigationPath = preserveQueryParam(
      ROUTE_PATH[ROUTE_NAME.RESULTS],
      { extra: matchFlow === MATCH_FLOWS.BASIC ? [REMATCH_URL_SEARCH_PARAM, 1] : [] },
    );
  }
  batch(() => {
    dispatch(sendFormSuccess());
    dispatch(sendVerificationCodeSuccess());
    dispatch(ReactRouter.push(finalNavigationPath));
    if (isSplashNeeded) {
      dispatch(hideBackdrop({ minDisplayTime: 5000 }));
    }
  });
};

const requestLogin = (values, type, redirectPath) => async (dispatch, getState) => {
  const queryParameters = queryString.parse(window.location.search);
  const { customerRef } = queryParameters;
  const isCustomerRefFlow = !!(customerRef && queryParameters.condition);

  const { profile: { data: profile } } = getState();

  // update medical profile case
  let isTempProfileLinking = !!profile.personal.profile_id;

  dispatch(sendFormRequest());

  let result = null;
  try {
    const requestData = {
      ...values,
      personal: {
        // we don't replace existing user profile with empty one for isCustomerRefFlow
        profile_id: (isCustomerRefFlow ? null : profile.personal.profile_id) || null,
      },
    };
    result = await authenticationApi.login(requestData, type);
  } catch (ex) {
    console.error(ex);
    dispatch(userAuthenticationFailure('authentication.login_submit.something_went_wrong'));
    return;
  }

  const navigationPath = ROUTE_PATH[ROUTE_NAME.RESULTS];

  if (result?.success && type === AUTH_TYPE.MAGIC_LINK.toLowerCase()) {
    // login with token (LOGIN_TOKEN_URL_SEARCH_PARAM)
    dispatch(sendFormSuccess());
    app.sendGoogleAnalyticsEvent(GA_LOGIN_CATEGORY, GA_LOGIN_EVENTS.LOGIN_SUCCESS_TOKEN);
    const { user } = result.data;
    setTokens(result.data.access_token, result.data.refresh_token);
    setSession(user);
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));

    window.location = redirectPath ?? navigationPath;
    return;
  }

  if (result && !result.success && result?.data?.user_wix_id) {
    // Unable to update medical profile. Resend request without updating profile. Load last version.

    isTempProfileLinking = false;
    dispatch(resetUploadedQuestionnaireFiles());

    try {
      result = await authenticationApi.login(values);
    } catch (ex) {
      console.error(ex);
      return;
    }
  }

  // if response has an error
  if (!result?.success) {
    if (AUTH_TYPE.GOOGLE === type?.toUpperCase()) {
      dispatch(userAuthenticationFailure());
      dispatch(ReactRouter.push(assembleRoutePath({
        pathname: ROUTE_PATH[ROUTE_NAME.LOGIN],
        searchParameters: {
          [AUTH_TYPE_URL_SEARCH_PARAM]: AUTH_TYPE.EMAIL,
          [AUTH_EMAIL_URL_SEARCH_PARAM]: result?.response?.data?.email,
        },
      })));
      return;
    }

    if (result.isExpiredPassword) {
      dispatch(userAuthenticationFailure('login.form.password.expired_message'));
      dispatch(ReactRouter.push(`${ROUTE_PATH[ROUTE_NAME.NEW_PASSWORD]}?token=${result.token}`));
      return;
    }

    if (result.message === 'Too many requests') {
      dispatch(userAuthenticationFailure('login.form.lockout_message'));
      return;
    }

    console.error('Error occurred, while sending login request');
    dispatch(userAuthenticationFailure('authentication.error.wrong_auth_data'));
    return;
  }

  if (!values.info.email && !type) {
    dispatch(setPhoneConfirmationResponse(result.data));
    dispatch(sendFormSuccess());
    return;
  }

  setTokens(result.data.access_token, result.data.refresh_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER, result.data.user);
  const user = JSON.parse(result.data.user);
  setSession(user);

  if (isCustomerRefFlow) {
    // corner case. It is going to be used once in a while
    try {
      const updatedUser = await authenticationApi.updateUserCustomerRef({ customerRef });
      await intercom.update({
        user: updatedUser,
      });
    } catch (ex) {
      console.error('requestLogin updateUserCustomerRef error', ex);
    }
  }
  // Successfully logged-in user considered as non-shadow (shadow user - created by HCP user)
  try {
    await intercom.updateUserAttributes(
      {
        shadow_patient: false,
      },
      user.user_wix_id,
    );
  } catch (ex) {
    console.error('requestLogin updateUserAttributes error', ex);
  }

  dispatch(setUserData(formLoginResponseProfile(user)));
  dispatch(userAuthenticationSuccess(user));

  dispatch(finishLogin({
    navigationPath: redirectPath ?? navigationPath,
    matchFlow: isTempProfileLinking ? MATCH_FLOWS.BASIC : MATCH_FLOWS.LATEST,
  }));
};

// is used for sign up and sing in processes
// eslint-disable-next-line consistent-return
const sendVerificationCode = (values, isSingUp, redirectPath) => async (dispatch, getState) => {
  const queryParameters = queryString.parse(window.location.search);
  const { customerRef } = queryParameters;
  // for SingUp case it is supposed that customerRef is sent with new profile
  const isCustomerRefFlow = !isSingUp && !!(customerRef && queryParameters.condition);
  const { profile: { data: profile } } = getState();
  let result = null;

  const requestData = {
    ...values,
    // we don't replace existing user profile with empty one for isCustomerRefFlow
    profile_id: (isCustomerRefFlow ? null : profile.personal.profile_id) || null,
  };

  // update medical profile case
  let isTempProfileLinking = !!profile.personal.profile_id;

  dispatch(sendVerificationCodeRequest());

  try {
    result = await authenticationApi.verify(requestData);
  } catch (ex) {
    console.error(ex);
  }

  if (result && !result.success && result?.data?.user_wix_id) {
    // Unable to update medical profile. Resend request without updating profile. Load last version.

    isTempProfileLinking = false;
    dispatch(resetUploadedQuestionnaireFiles());

    try {
      result = await authenticationApi.verify(values);
    } catch (ex) {
      console.error(ex);
    }
  }

  // if response has an error
  if (!result?.success) {
    console.error('Error occurred, while sending confirmation code');
    dispatch(userAuthenticationFailure('authentication.error.wrong_code'));
    return result?.message || 'Something went wrong';
  }

  setTokens(result.data.access_token, result.data.refresh_token);
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER, result.data.user);
  const user = JSON.parse(result?.data?.user);

  if (isCustomerRefFlow) {
    // corner case. It is going to be used once in a while
    try {
      const updatedUser = await authenticationApi.updateUserCustomerRef({ customerRef });
      await intercom.update({
        user: updatedUser,
      });
    } catch (ex) {
      console.error('sendVerificationCode updateUserCustomerRef error', ex);
    }
  }
  if (!isSingUp) {
    // Successfully logged-in user considered as non-shadow (shadow user - created by HCP user)
    try {
      await intercom.updateUserAttributes(
        {
          shadow_patient: false,
        },
        user.user_wix_id,
      );
    } catch (ex) {
      console.error('sendVerificationCode updateUserAttributes error', ex);
    }
  }

  let usersData;
  try {
    usersData = await dispatch(getUser());
    if (!usersData) {
      throw new Error('empty user response');
    }
  } catch (ex) {
    console.error('after login operation error.', ex);
  }

  dispatch(setUserData(formLoginResponseProfile(user)));
  dispatch(userAuthenticationSuccess(user));
  sendAuthEvent(AUTH_TYPE.PHONE);

  app.sendGoogleAnalyticsEvent(GA_LOGIN_CATEGORY, GA_LOGIN_EVENTS.LOGIN_SUCCESS);

  let navigationPathOnFinish;
  if (isSingUp) {
    navigationPathOnFinish = `${ROUTE_PATH[ROUTE_NAME.RESULTS]}?${queryString.stringify({
      ...queryString.parse(window.location.search),
      toSubmit: 1,
    })}`;
  } else {
    navigationPathOnFinish = preserveQueryParam(ROUTE_PATH[ROUTE_NAME.RESULTS]);
  }

  await dispatch(finishLogin({
    navigationPath: redirectPath ?? navigationPathOnFinish,
    matchFlow: isTempProfileLinking ? MATCH_FLOWS.BASIC : MatchFlow.getMatchFlow(),
  }));
  setSession(user);
};

const requestPasswordRecovery = (email) => async (dispatch) => {
  let result = null;

  dispatch(sendFormRequest());

  try {
    result = await authenticationApi.forgotPassword(email);
  } catch (ex) {
    console.error(ex);
  }

  // if response has an error
  if (!result || !result.success) {
    console.error('Error occurred, while sending request');
    // dispatch(userAuthenticationFailure('authentication.error.wrong_email'));
    dispatch(userAuthenticationFailure(null));
    return;
  }

  dispatch(userAuthenticationSuccess());
  dispatch(sendFormSuccess());
};

const requestPasswordReset = (data) => async (dispatch) => {
  let result = null;

  dispatch(sendFormRequest());

  try {
    result = await authenticationApi.resetPassword(data);
  } catch (ex) {
    console.error(ex);
  }

  // if response has an error
  if (!result || !result.success) {
    console.error('Error occurred, while sending request');
    dispatch(userAuthenticationFailure('authentication.error.password_reset_failure'));
    return;
  }

  dispatch(userAuthenticationSuccess());
  dispatch(sendFormSuccess());
};

const setSuperUser = () => {
  localStorage.setItem(LOCAL_STORAGE_KEYS.SUPER_USER, 'true');
};

const setPartner = (partner) => ({ type: PARTNER_CONSTANTS.SET_PARTNER, payload: partner });

const setInternal = () => ({ type: INTERNAL_CONSTANTS.SET_INTERNAL });

const getUser = () => {
  return async (dispatch, getState) => {
    let result = null;
    const { main } = getState();

    dispatch(requestUser());

    try {
      result = await authenticationApi.getUser({
        lang: main?.lang,
      });
    } catch (ex) {
      console.error('Exception:', ex);
      return;
    }

    if (!result || !result.info || !result.personal || !result.condition_profile) {
      console.error('Server response is invalid:', result);
      dispatch(failureUser(`Server response is invalid: ${JSON.stringify(result)}`));
      dispatch(ReactRouter.push(ROUTE_PATH[ROUTE_NAME.LOGIN]));
      return;
    }

    const actionsToDispatch = [];
    const queueActionToDispatch = (action) => {
      actionsToDispatch.push(action);
    };

    const {
      info: {
        user_type = null,
      },
      personal: { providerCode = null },
    } = result;

    const user = formLocalStorageUserFromServerUser(result);

    const country = result.info.country || null;

    localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));

    // provider must be set before we call getConfiguration
    queueActionToDispatch(providerActions.setProvider(providerCode));

    result.info = Object.assign(user, {
      country,
    });

    result.condition_profile.user_type = user_type;

    queueActionToDispatch(setUserData(result));

    if (country) {
      queueActionToDispatch(setGeoLocation(country || {}));
    }

    queueActionToDispatch(successUser(user));
    queueActionToDispatch(userAuthenticationSuccess(user));

    batch(() => {
      actionsToDispatch.forEach((action) => {
        dispatch(action);
      });
    });

    // eslint-disable-next-line consistent-return
    return result;
  };

  function requestUser() {
    return { type: CONSTANTS.GET_USER_REQUEST };
  }

  function successUser(user) {
    return { type: CONSTANTS.GET_USER_SUCCESS, payload: user };
  }

  function failureUser(error) {
    return { type: CONSTANTS.GET_USER_FAILURE, payload: error };
  }
};

const logout = (path = '/login') => (dispatch, getState) => {
  clearAuthDataInLocalStorage();

  const { appConfiguration: { providerSpecific } } = getState();
  const providerCode = providerSpecific?.code;

  const finalPath = providerCode ? `${path}?${PROVIDER_URL_SEARCH_PARAM}=${providerCode}` : path;

  batch(() => {
    dispatch({ type: CONSTANTS.LOGOUT });
    dispatch(clearGeoLocation());
  });

  window.location = finalPath;
  // temp solution due to app does not handle logout; page reload is required
  // dispatch(ReactRouter.replace(path));
};

const updateUser = (
  { user = null, loggedIn = true },
  meta = {},
// eslint-disable-next-line arrow-body-style
) => {
  return async (dispatch, getState) => {
    if (!user) {
      return;
    }

    let response = null;
    if (
      loggedIn
      && user.personal
      && user.personal.user_wix_id
      && user.personal.patient_id
      && user.personal.profile_id
    ) {
      if (user.info.user_type) {
        const sessionData = getLocalStorageUser();
        if (sessionData && !sessionData?.user_type) {
          sessionData.user_type = user.info.user_type;
          localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(sessionData));
        }
      }

      if (user.info?.phone && !user.info?.phone.startsWith('+')) {
        user.info.phone = `+${user.info.phone}`;
        user.info.phone = phone(user.info.phone).phoneNumber;
      }

      dispatch(requestUserUpdate(meta));

      if (user?.condition_profile) {
        const { qstack } = user.condition_profile;
        if (isArray(qstack) && qstack.find((a) => a === 'drugs_received')) {
          qstack.splice(qstack.indexOf('drugs_received'), 1);
          user.condition_profile.qstack = qstack;
        }
        if (user?.condition_profile?.treatments?.length) {
          user.condition_profile.treatments = user.condition_profile.treatments.filter(
            (a) => a?.drugs_received?.length,
          );
        }
      }

      const { profile: { originalData: oldUser } } = getState();

      if (oldUser) {
        // some places in the code ignore to pass oldUser
        // can be not reliable

        // if oldUser.userWixId is missing we can suggest that a user is just authenticated
        if (!getUserWixId(oldUser) || !isEqual(user?.condition_profile?.treatments, oldUser.condition_profile?.treatments)) {
          const lastTreatment = user?.condition_profile?.treatments?.[0];
          if (lastTreatment && app?.intercom?.isBooted) {
            if (isArray(lastTreatment.drugs_received)) {
              app.intercom.updateUserAttributes({
                last_treatment: lastTreatment.drugs_received.join(' + '),
                last_treatment_updating_date: new Date().toLocaleDateString(),
                last_treatment_status: lastTreatment?.treatment_stop_reason,
              });
            }
          }
        }
      }

      try {
        // omit patients to not have 413 response
        response = await authenticationApi.updateUser(omit(user, ['patients']));

        if (oldUser) {
          // we don't want to delay user update as it is the main request
          updateUserChanges(oldUser, user)
            .then((userConfigurationToUpdate) => {
              dispatch(appConfigurationActions.updateUserConfiguration(userConfigurationToUpdate));
            })
            .catch((ex) => {
              console.error('updateUserChanges(oldUser, user) ex', ex);
            });
        }
      } catch (ex) {
        console.error(`Error occurred, while updating user profile: ${(ex && ex.message) || ex}`);

        if (axiosExceptionToFailure(ex).code === RESPONSE_ERROR.PHONE_IS_USED) {
          app.sendGoogleAnalyticsEvent(
            GA_GENERAL_CATEGORY,
            GA_GENERAL_POPUPS_EVENTS.PHONE_NUMBER_UPDATE_DUPLICATION_FAILURE,
          );

          app.setPopup(POPUP.PHONE_IS_ALREADY_USED, { isChild: true });
        }

        dispatch(failureUserUpdate(axiosExceptionToFailure(ex), meta));
        return;
      }

      if (!response || !response.info) {
        const message = `Wrong response occurred, while updating user profile: ${response}`;
        console.error(message);
        dispatch(failureUserUpdate(message, meta));
        return;
      }
    }

    dispatch(setUserData(response ?? user, meta));
    dispatch(successUserUpdate(meta));
  };
};

const goToQuestionnaire = ({ condition, conditionSubType, user }) => {
  return async (dispatch) => {
    const {
      user_wix_id, email, family_name, phone: phoneValue, partner, user_type,
    } = user || {};

    const questionnaire = await getQuestionnaireQuestions(condition);

    if (user_wix_id && email) {
      // new profile for existing user
      let result = null;
      const data = {
        info: {
          user_wix_id,
          country: null,
          email,
          family_name: family_name || null,
          phone: phoneValue || null,
          partner: partner || null,
          user_type: user_type || null,
        },
        personal: {
          user_wix_id,
          profile_id: null,
          patient_id: null,
          condition,
          gender: null,
          dob: null,
          travel_limit: null,
        },
        condition_profile: {},
      };

      dispatch(requestCreateUser());

      try {
        result = await authenticationApi.createFullUser(data);
      } catch (ex) {
        console.error('Exception:', ex);
        dispatch(failureCreateUser(`Error occurred, while creating user data: ${ex}`));
        return;
      }

      // eslint-disable-next-line no-prototype-builtins
      result = result.hasOwnProperty('result') ? result.result : result;

      if (
        !result
        || !(
          result.personal
          && result.personal.profile_id
          && result.personal.patient_id
          && result.condition_profile
          && Object.keys(result.condition_profile).length
        )
      ) {
        const message = 'Error occurred, while creating user data';
        console.error(message);
        dispatch(failureCreateUser(message));
        return;
      }

      const { profile_id, patient_id } = result.personal;

      batch(() => {
        dispatch(setProfileId(profile_id));
        dispatch(setPatientId(patient_id));
        dispatch(successCreateUser());
      });
    }

    batch(() => {
      dispatch({ type: CONSTANTS.SET_CONDITION, payload: { condition, conditionSubType } });
      dispatch(setIsFetching(false));
      dispatch(ReactRouter.replace(createQuestionnaireRedirectURL(questionnaire)));
    });
  };

  function setPatientId(value) {
    return { type: CONSTANTS.SET_PATIENT_ID, payload: value };
  }

  function requestCreateUser() {
    return { type: CONSTANTS.CREATE_USER_REQUEST };
  }

  function successCreateUser() {
    return { type: CONSTANTS.CREATE_USER_SUCCESS };
  }

  function failureCreateUser(error) {
    return { type: CONSTANTS.CREATE_USER_FAILURE, payload: error };
  }
};

const startUserRegistration = ({
  profileData,
  quickSignup = false,
  isModalSignup = false,
}) => async (dispatch, getState) => {
  dispatch({ type: CONSTANTS.CREATE_USER_REQUEST });
  const { appConfiguration } = getState();
  profileData.personal.providerId = appConfiguration.providerSpecific.id;
  // Create temp user data without "user_wix_id" for non-authorized user
  let tempUserCreationResult;
  try {
    tempUserCreationResult = await authenticationApi.createTempUser(profileData);
  } catch (ex) {
    console.error('Exception:', ex);
    batch(() => {
      dispatch({
        type: CONSTANTS.CREATE_USER_FAILURE,
        payload: `Error occurred, while creating user data: ${ex}`,
      });
      dispatch(setIsFetching(false));
    });
    return;
  }

  dispatch({ type: CONSTANTS.CREATE_USER_SUCCESS });

  if (!tempUserCreationResult) {
    console.error('Error occurred, while creating/updating user data');
    batch(() => {
      dispatch(ReactRouter.push(ROUTE_PATH[ROUTE_NAME.HOME]));
      dispatch(setIsFetching(false));
    });
    return;
  }

  // eslint-disable-next-line no-prototype-builtins
  tempUserCreationResult = tempUserCreationResult.hasOwnProperty('result')
    ? tempUserCreationResult.result : tempUserCreationResult;

  if (
    !tempUserCreationResult
    || !(
      tempUserCreationResult.personal
      && tempUserCreationResult.personal.profile_id
      && tempUserCreationResult.personal.patient_id
      && tempUserCreationResult.condition_profile
      && (profileData.personal.customerRef || Object.keys(tempUserCreationResult.condition_profile).length)
    )
  ) {
    console.error('Error occurred, while creating/updating user data');
    dispatch(setIsFetching(false));
    return;
  }

  const {
    personal: {
      profile_id: profileId,
      patient_id: patientId,
    },
    // returned for provider registration
    trialsCount = null,
  } = tempUserCreationResult;

  batch(() => {
    dispatch({ type: CONSTANTS.SET_PROFILE_ID, payload: profileId });
    dispatch({ type: CONSTANTS.SET_TRIALS_COUNT, payload: trialsCount });
    dispatch({ type: CONSTANTS.SET_PATIENT_ID, payload: patientId });
    dispatch(setUserData({
      personal: {
        condition: tempUserCreationResult.personal?.condition,
        customerRef: profileData?.personal?.customerRef,
      },
    }));
    let signUpPath;
    if (isModalSignup) {
      signUpPath = preserveQueryParam(
        ROUTE_PATH[ROUTE_NAME.SIGN_UP],
        {
          omit: [QUESTIONNAIRE_IN_PROGRESS_URL_SEARCH_PARAM],
          extra: [[SIGNUP_TYPE_URL_SEARCH_PARAM, 'modal_signup']],
        },
      );
    } else {
      signUpPath = preserveQueryParam(
        ROUTE_PATH[ROUTE_NAME.SIGN_UP],
        {
          omit: [QUESTIONNAIRE_IN_PROGRESS_URL_SEARCH_PARAM],
          extra: quickSignup ? [[SIGNUP_TYPE_URL_SEARCH_PARAM, 'quick_signup']] : null,
        },
      );
    }
    dispatch(ReactRouter.push(signUpPath));

    dispatch(setIsFetching(false));
  });
};

const startCustomerRefUserRegistration = ({ profile }) => (dispatch) => {
  dispatch(setIsFetching(true));

  dispatch(startUserRegistration({
    profileData: profile,
    isModalSignup: true,
  }));
};

const updateUserCustomerRef = ({ customerRef }) => async (dispatch) => {
  dispatch(setIsFetching(true));

  try {
    const updatedUser = await authenticationApi.updateUserCustomerRef({ customerRef });

    localStorage.setItem(
      LOCAL_STORAGE_KEYS.USER,
      JSON.stringify(formLocalStorageUserFromServerUser(updatedUser)),
    );

    await intercom.update({ user: updatedUser });
  } catch (ex) {
    console.error(`updateUserCustomerRef: ${(ex.message)}`);
  } finally {
    batch(() => {
      dispatch(setIsFetching(false));
      dispatch(ReactRouter.push(ROUTE_PATH[ROUTE_NAME.FEED]));
    });
  }
};

// is not used; caller code is broken
const newProfile = () => (dispatch, getState) => {
  const { profile: { data: profile } } = getState();
  if (!profile?.info) {
    // should be invariant
    return;
  }
  batch(() => {
    dispatch(setNewProfile(profile.info));
    dispatch(clearMatches());
    dispatch(clearGeoLocation());
    dispatch(setIsNewProfile(true));
    dispatch(ReactRouter.push(`${ROUTE_PATH[ROUTE_NAME.HOME]}?${USE_QUESTIONNAIRE_SELECTOR_URL_SEARCH_PARAM}=1`));
  });
};

export const authenticationActions = {
  requestSignup,
  requestLogin,
  requestPasswordRecovery,
  requestPasswordReset,
  sendVerificationCode,
  userAuthenticationFailure,
  userAuthenticationSuccess,
  setFormValidity,
  resetFormValidity,
  resetPhoneConfirmationResponse,
  resetLoggedIn,
  getUser,
  setTokens,
  logout,
  updateUser,
  goToQuestionnaire,
  startUserRegistration,
  newProfile,
  setPartner,
  setInternal,
  setSuperUser,
  finishLogin,
  setUserData,
  upsertUserPatient,
  startCustomerRefUserRegistration,
  updateUserCustomerRef,
  updateUserAfterSignup,
};
